// Shapes the dashboard shell reads (Plan 4b Task 1). Minimal on purpose: the data routes (Tasks 2–5) own their own
// response contracts and may extend these.
import { z } from 'zod';
import { Id } from './accounts.ts';

/** One option of the global "filtrar por titular" control: an AnmEntity the customer monitors. */
export const AnmEntityOption = z.object({
  id: Id,
  /** The holder's display name (title case). */
  label: z.string(),
  processCount: z.number().int().nonnegative(),
});
export type AnmEntityOption = z.infer<typeof AnmEntityOption>;

// Freshness (A8) is served by the product routes: `Freshness` / `SourceFreshness` in product.ts.

/** `GET /app/anm-entities` — the AnmEntities the active customer can see privately (held or subrogated), for the titular filter. */
export const AnmEntityListResponse = z.object({ items: z.array(AnmEntityOption) });
export type AnmEntityListResponse = z.infer<typeof AnmEntityListResponse>;

/** `GET /app/me` (apps/api routes/account.ts): the login and the customers it acts for. */
export const MeCustomer = z.object({
  id: Id,
  name: z.string(),
  kind: z.enum(['cpf', 'cnpj']),
  status: z.enum(['active', 'inadimplente', 'deleted']),
  /** S-z: the account type — a consultoria (true) or a conta comum */
  isConsultant: z.boolean(),
  /** S-z: the partner program (opt-in): null = not applied */
  partnerStatus: z.enum(['pending', 'approved', 'rejected', 'suspended']).nullable(),
  role: z.enum(['owner', 'member']),
  capabilities: z.array(z.string()),
});
export type MeCustomer = z.infer<typeof MeCustomer>;
export const MeResponse = z.object({
  user: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    phoneVerified: z.boolean().nullable(),
    twoFactorEnabled: z.boolean().nullable(),
  }),
  customers: z.array(MeCustomer),
});
export type MeResponse = z.infer<typeof MeResponse>;

/** `GET /app/carteira`: the consultant's subrogated subscriptions with each one's risk (Carteira artboard). */
export const CarteiraRisk = z.object({
  processCount: z.number().int().nonnegative(),
  areaHa: z.number().nonnegative(),
  ufs: z.array(z.string()),
  /** Live obligations triaged "agir agora" (not resolved). */
  actNowCount: z.number().int().nonnegative(),
  /** The most urgent dated live obligation: the oldest overdue one, else the nearest (daysLeft < 0 = overdue). */
  nextDue: z.object({ dueOn: z.string(), daysLeft: z.number().int(), kind: z.string() }).nullable(),
  /** Live dated obligations already past their date. */
  overdueCount: z.number().int().nonnegative(),
  /** Live RESPOND_TO_CONDITION obligations ("exigências abertas"). */
  openRequirements: z.number().int().nonnegative(),
  /** Live PAY_FEE obligations due within 90 days or already late (TAH and fees — amounts are not known yet). */
  feesDueSoon: z.number().int().nonnegative(),
  phases: z.array(z.object({ phase: z.string(), count: z.number().int().nonnegative() })),
});
export const CarteiraItem = z.object({
  subscriptionId: Id,
  anmEntityId: Id,
  /** The customer that holds and pays for the subscription (its display name, S-p). */
  holderName: z.string(),
  /** The ANM holder (AnmEntity) it monitors. */
  anmEntityLabel: z.string(),
  status: z.enum(['pending', 'trial', 'active', 'cancelled']),
  plan: z.object({ tierCode: z.string(), modules: z.array(z.string()) }).nullable(),
  /** Hidden by the consultant (listed only with `?hidden=true`, to un-hide). */
  hidden: z.boolean(),
  warning: z.string().nullable(),
  /** Null when the holder is inadimplente or the subscription grants no access. */
  risk: CarteiraRisk.nullable(),
});
export const CarteiraResponse = z.object({ items: z.array(CarteiraItem) });
export const CarteiraQuery = z.object({ hidden: z.enum(['true', 'false']).optional() });
/** `POST /app/carteira/:subscriptionId/hide`. */
export const CarteiraHideResponse = z.object({ ok: z.literal(true) });
export type CarteiraRisk = z.infer<typeof CarteiraRisk>;
export type CarteiraItem = z.infer<typeof CarteiraItem>;
export type CarteiraResponse = z.infer<typeof CarteiraResponse>;
